import React from 'react';
import { Form, Radio, Checkbox, Input, Typography, Space, DatePicker, Select, FormInstance } from 'antd';
import { QuestionnaireField, QuestionnaireSchema } from './QuestionnaireSchema';

const { Title, Paragraph } = Typography;

interface Props {
    schema: QuestionnaireSchema;
    form: FormInstance;
    // AntD derives each input's DOM id from the form name + field key. Pass a name
    // when a second copy of a questionnaire can be mounted at the same time (e.g.
    // the profile-completion wizard opening over a profile that already renders one)
    // so the ids stay unique and each label binds to its own input.
    formName?: string;
}

// Renders a client-configurable questionnaire into an Ant Design form. Each
// field becomes a Form.Item named by its `key`, so form.validateFields() yields
// the same shape the previous hardcoded forms produced (heartCondition,
// medicalConditions: string[], agreement: boolean, …) and schemaToAnswers can
// serialise it back to the persisted {question, answer} pairs.
const DynamicQuestionnaire: React.FC<Props> = ({ schema, form, formName }) => (
    <Form form={form} name={formName} layout="vertical">
        {schema.sections.map((section) => (
            <div key={section.key}>
                {section.title && <Title level={2}>{section.title}</Title>}
                {section.description && <Paragraph>{section.description}</Paragraph>}
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {section.fields.map((field) => (
                        <React.Fragment key={field.key}>{renderField(field)}</React.Fragment>
                    ))}
                </Space>
            </div>
        ))}
    </Form>
);

function requiredRule(field: QuestionnaireField, message: string) {
    return field.required ? [{ required: true, message }] : [];
}

function renderField(field: QuestionnaireField): React.ReactNode {
    switch (field.type) {
        case 'info':
            return <Paragraph>{field.label}</Paragraph>;

        case 'boolean':
            // The label is the checkbox text (matches the original agreement field).
            return (
                <Form.Item
                    name={field.key}
                    valuePropName="checked"
                    rules={requiredRule(field, 'Please confirm to continue')}
                >
                    <Checkbox>{field.label}</Checkbox>
                </Form.Item>
            );

        case 'radio':
            return (
                <Form.Item name={field.key} label={field.label} rules={requiredRule(field, 'Please select an option')}>
                    <Radio.Group>
                        {(field.options ?? []).map((o) => (
                            <Radio key={o.value} value={o.value}>{o.label}</Radio>
                        ))}
                    </Radio.Group>
                </Form.Item>
            );

        case 'checkboxGroup':
            return (
                <Form.Item name={field.key} label={field.label} rules={requiredRule(field, 'Please select at least one option')}>
                    <Checkbox.Group>
                        <Space direction="vertical">
                            {(field.options ?? []).map((o) => (
                                <Checkbox key={o.value} value={o.value}>{o.label}</Checkbox>
                            ))}
                        </Space>
                    </Checkbox.Group>
                </Form.Item>
            );

        case 'select':
            return (
                <Form.Item name={field.key} label={field.label} rules={requiredRule(field, 'Please select an option')}>
                    <Select options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))} />
                </Form.Item>
            );

        case 'textarea':
            return (
                <Form.Item name={field.key} label={field.label} rules={requiredRule(field, 'This field is required')}>
                    <Input.TextArea />
                </Form.Item>
            );

        case 'date':
            // The declaration date is auto-stamped with the completion date at
            // submit time (see schemaToAnswers), so it's never a required user
            // input — leave it optional so a blank value doesn't block the form,
            // while still letting the operator override it.
            return (
                <Form.Item name={field.key} label={field.label}>
                    <DatePicker />
                </Form.Item>
            );

        case 'text':
        default:
            return (
                <Form.Item name={field.key} label={field.label} rules={requiredRule(field, 'This field is required')}>
                    <Input />
                </Form.Item>
            );
    }
}

export default DynamicQuestionnaire;
